import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { UserRole } from 'shared';
import { trpc } from '@/trpc/client';

// API returns dates as strings, so we need a separate type
interface ApiUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  role: UserRole;
  primaryLocationId: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: ApiUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setAuthData: (user: ApiUser, token: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Non-sensitive marker: tells us whether the user had an active session
// so we can show "session expired" vs "please log in" on UNAUTHORIZED
const SESSION_MARKER_KEY = 'vitbikes_session_active';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null);
  // Token kept in-memory only (not in localStorage) — auth is handled by httpOnly cookie
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loginMutation = trpc.user.login.useMutation();
  // Always run /me — the httpOnly cookie is sent automatically by the browser
  const { data: currentUser, isLoading: isUserLoading, error: meError } = trpc.user.me.useQuery(undefined, {
    enabled: true,
    retry: false,
  });

  // Update user when /me returns data
  useEffect(() => {
    if (currentUser) {
      setUser(currentUser as ApiUser);
    }
  }, [currentUser]);

  // Handle auth state on initial load
  useEffect(() => {
    if (!isUserLoading) {
      if (!currentUser && meError) {
        const trpcError = meError as { data?: { code?: string } };
        if (trpcError?.data?.code === 'UNAUTHORIZED') {
          // Show "session expired" only if the user had an active session before
          if (localStorage.getItem(SESSION_MARKER_KEY)) {
            localStorage.removeItem(SESSION_MARKER_KEY);
            localStorage.setItem('vitbikes_session_expired', 'true');
          }
          setToken(null);
          setUser(null);
        }
        // For other errors (network, server restart), keep current auth state
      }
      setIsLoading(false);
    }
  }, [currentUser, isUserLoading, meError]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginMutation.mutateAsync({ email, password });
    // Cookie is set by server — just update client state
    if (result.token) {
      localStorage.setItem(SESSION_MARKER_KEY, 'true');
      setToken(result.token);
      setUser(result.user as ApiUser);
    }
    // requires2fa=true: caller handles 2FA flow (setAuthData called later)
  }, [loginMutation]);

  const logoutMutation = trpc.user.logout.useMutation();

  const logout = useCallback(() => {
    // Server clears the httpOnly cookie; client clears local state
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        localStorage.removeItem(SESSION_MARKER_KEY);
        setToken(null);
        setUser(null);
        window.location.href = '/login';
      },
    });
    // Fallback redirect if mutation doesn't settle quickly
    setTimeout(() => {
      localStorage.removeItem(SESSION_MARKER_KEY);
      window.location.href = '/login';
    }, 1000);
  }, [logoutMutation]);

  // Called by 2FA flow after successful verification — cookie already set by server
  const setAuthData = useCallback((newUser: ApiUser, newToken: string) => {
    localStorage.setItem(SESSION_MARKER_KEY, 'true');
    setToken(newToken);
    setUser(newUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        setAuthData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
